import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Building2, FileDown, Loader2, FileSpreadsheet, FileText, FileType, PieChart as PieIcon } from "lucide-react";
import { StatusTotalCards } from "@/components/StatusTotalCards";

export function EmpresaWelcomeCard({ empresa }) {
  if (!empresa) return null;
  return (
    <div className="flex items-center gap-4 bg-gradient-to-r from-orange-50 to-white border border-orange-200 rounded-xl p-4 shadow-sm" data-testid="empresa-welcome-card">
      {empresa.logo_base64 ? (
        <img src={empresa.logo_base64} alt="" className="w-14 h-14 object-contain rounded-lg bg-white border border-orange-100" data-testid="empresa-welcome-logo" />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600"><Building2 className="w-7 h-7" /></div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase text-orange-600 font-semibold tracking-wider">Bem-vindo à</div>
        <div className="text-xl font-extrabold truncate" data-testid="empresa-welcome-nome">{empresa.nome}</div>
        {empresa.cnpj && <div className="text-xs text-muted-foreground mono">CNPJ: {empresa.cnpj}</div>}
      </div>
      {empresa.segmento && (
        <span className="hidden sm:inline text-xs uppercase tracking-widest text-orange-700 bg-orange-100 px-2 py-1 rounded">{empresa.segmento}</span>
      )}
    </div>
  );
}

// Exportar (CSV/XLSX/PDF de dados com filtros ativos + snapshot visual do painel).
export function DashboardExportMenu({ exportingRep, exportingPdf, filtrosAtivos, onExportReport, onExportPainel }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="btn-export-menu" disabled={!!exportingRep} title="Baixar relatório com os filtros ativos">
          {exportingRep
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Baixando {exportingRep.toUpperCase()}…</>
            : <><FileDown className="w-4 h-4 mr-2" />Exportar</>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs">Reflete filtros ativos {filtrosAtivos ? <span className="text-orange-600">•</span> : null}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onExportReport("pdf")} data-testid="btn-export-report-pdf" className="cursor-pointer">
          <FileType className="w-4 h-4 mr-2 text-rose-600" />PDF (Relatório)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onExportReport("xlsx")} data-testid="btn-export-report-xlsx" className="cursor-pointer">
          <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onExportReport("csv")} data-testid="btn-export-report-csv" className="cursor-pointer">
          <FileText className="w-4 h-4 mr-2 text-slate-600" />CSV
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onExportPainel} disabled={exportingPdf} data-testid="btn-export-dashboard-pdf" className="cursor-pointer" title="Gerar snapshot visual do painel (para reuniões)">
          {exportingPdf
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando…</>
            : <><FileDown className="w-4 h-4 mr-2 text-orange-600" />PDF do Painel (Visual)</>}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DashboardTabs({ tab, setTab }) {
  return (
    <Tabs value={tab} onValueChange={setTab} className="w-auto">
      <TabsList data-testid="dashboard-tabs">
        <TabsTrigger value="empresa" data-testid="tab-empresa">Empresa Atual</TabsTrigger>
        <TabsTrigger value="consolidado" data-testid="tab-consolidado">Consolidado (Todas)</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

// Widget: panorama rápido dos últimos 7 dias com comparativo (fev/2026).
export function Totals7dWidget({ totals7d }) {
  if (!totals7d || !(totals7d.total > 0)) return null;
  const prev = totals7d.previous;
  const up = totals7d.total >= (prev?.total || 0);
  return (
    <Card data-testid="dashboard-totals-widget" className="border-orange-100">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PieIcon className="w-4 h-4 text-orange-600" />
          Panorama · últimos 7 dias
          <span className="text-xs font-normal text-muted-foreground ml-auto" data-testid="widget-total-7d">
            Total: <b>{totals7d.total}</b>
            {prev && (
              <span className="ml-2">
                · vs anterior: <b>{prev.total}</b>
                {prev.total > 0 && (
                  <span className={`ml-1 font-bold ${up ? "text-emerald-700" : "text-rose-700"}`}>
                    {up ? "▲" : "▼"}{Math.abs(Math.round(((totals7d.total - prev.total) / prev.total) * 100))}%
                  </span>
                )}
              </span>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <StatusTotalCards totals={totals7d} compact testIdPrefix="widget-status" />
      </CardContent>
    </Card>
  );
}
